// dependencies
import * as Sentry from "@sentry/react";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";

// schema
import { AiSettingsSchema } from "@performyard/models";

//components
import { AdminContainer, AdminTitle } from "../../../../components/Admin";
import Button from "../../../../components/Button";
import DeletableChip from "../../../../components/Chip/templates/DeletableChip";
import Container from "../../../../components/Container";
import { Grid, GridCol } from "../../../../components/Grid";
import InputText from "../../../../components/Inputs/InputText";
import SelectInput from "../../../../components/Inputs/SelectInput";
import Toggle from "../../../../components/Inputs/ToggleSwitches/ThumbSwitch";
import PageTitle from "../../../../components/PageTitle";
import Spacer from "../../../../components/Spacer";
import Text from "../../../../components/Text";

// hooks
import { useAiSettings, useUpdateAiSettings } from "../../../../api/hooks";
import { getFeatureFlagQuery, useFeatureFlag } from "../../../../hooks/useFeatureFlag";
import useForm from "../../../../hooks/useForm";
import useToast from "../../../../hooks/useToast";

import { companyAISettingsQuery, currentUserQuery } from "../../../../api/queries";
import { dataLoader } from "../../../../api/queries/dataLoader";

// selectors
import { getAppWidth } from "../../../../selectors/app";

// utils
import { showError } from "../../../../utils/RequestReponse";

// pages
import ErrorPage from "../../../Error";
// styles
import css from "./AIAuthoringAssistant.module.scss";
// config
import { preferredWritingStyleOptions } from "./config";

// Data loader function
export const aIAuthoringAssistantSettingsLoader = () => {
	const queries = [companyAISettingsQuery, getFeatureFlagQuery];

	return dataLoader([[currentUserQuery], queries]);
};

const AIAuthoringAssistantSettings = () => {
	const { mutateAsync: updateAiSettings, isPending } = useUpdateAiSettings();
	const { data } = useAiSettings();
	// ai feature flag enabled
	const aiFeatureFlagEnabled = useFeatureFlag("ff-is-ai-enabled", false);
	const formQuestionAssistantFeatureFlagEnabled = useFeatureFlag(
		"ff-temp-ai-assisted-form-question-authoring",
		false
	);
	const width = useSelector(getAppWidth);

	const toast = useToast();

	const [enabled, setEnabled] = useState(data?.authoringAssistantEnabled);
	const [formQuestionEnabled, setFormQuestionEnabled] = useState(data?.formQuestionAssistantEnabled);
	const { state, updateForm, setNewDefaultValues, resetForm, hasUnsavedChanges } = useForm({
		schema: AiSettingsSchema.shape.authoringAssistantSettings,
		defaultValues: data?.authoringAssistantSettings,
	});
	const [disallowedWordsInput, setDisallowedWordsInput] = useState("");
	const [disallowedContainerWidth, setDisallowedContainerWidth] = useState<number | undefined>(0);
	const currentDisallowedWordListChipContainerWidth =
		document.getElementById("disallowed-chips-container")?.clientWidth;
	useEffect(() => {
		setDisallowedContainerWidth(currentDisallowedWordListChipContainerWidth);
	}, [width, currentDisallowedWordListChipContainerWidth]);

	// handle preferred writing style change
	const handlePreferredWritingStyleChange = (value) => {
		updateForm({
			...state,
			preferredWritingStyle: value,
		});
	};

	// toggles behave differently in that changes are saved without having to click the save button
	const createToggleHandler =
		(
			field: "authoringAssistantEnabled" | "formQuestionAssistantEnabled",
			setter: (val: boolean) => void,
			assistantName: string
		) =>
		async (val: boolean) => {
			try {
				await updateAiSettings({ [field]: val });
				setter(val);
				toast(`${assistantName} ${val ? "Enabled" : "Disabled"}.`, {
					type: "success",
					size: "large",
				});
			} catch {
				showError("Unable to save settings.");
			}
		};

	const handleEnabledToggle = createToggleHandler(
		"authoringAssistantEnabled",
		setEnabled,
		"Form Authoring Assistant"
	);
	const handleFormQuestionToggle = createToggleHandler(
		"formQuestionAssistantEnabled",
		setFormQuestionEnabled,
		"Form Question Assistant"
	);

	const discardChanges = () => {
		resetForm();
	};

	const handleSave = async () => {
		try {
			await updateAiSettings({ authoringAssistantSettings: state });
			setNewDefaultValues(state);
			toast("Your changes have been saved.", { type: "success", size: "large" });
		} catch {
			showError("Unable to save settings.");
		}
	};

	const handleAddDisallowedWord = () => {
		// add word to list if it doesn't already exist and it's not an empty string
		if (disallowedWordsInput && !state.disallowedWords.includes(disallowedWordsInput)) {
			updateForm({
				...state,
				disallowedWords: [...state.disallowedWords, disallowedWordsInput],
			});
		}
		setDisallowedWordsInput("");
	};

	const handleDeleteDisallowedWord = (word) => {
		updateForm({
			...state,
			disallowedWords: state.disallowedWords.filter((w) => w !== word),
		});
	};

	if (!aiFeatureFlagEnabled) {
		return <ErrorPage code={404} />;
	}

	return (
		<Sentry.ErrorBoundary fallback={undefined}>
			<Spacer height={4} />
			<AdminContainer>
				<PageTitle title="AI Rephrase Settings" />
				<AdminTitle>AI Rephrase</AdminTitle>
				<Spacer height={4} />
				<Grid cols={10} gap={2}>
					{/* left side of the page */}
					<GridCol span={4}>
						<Text size="paragraph-2" weight="semi-bold" color="neutral-9">
							AI Rephrase Assistant Settings
						</Text>
						<Spacer height={1} />
						<div className={css.description}>
							<Text size="paragraph-3" weight="regular" color="neutral-9">
								The AI Rephrase Assistants allow users to rephrase questions when creating review forms
								and to revise responses when answering text-based questions.
							</Text>
						</div>
						<Spacer height={3} />
					</GridCol>
					<GridCol span={1} />

					{/* right side of the page */}
					<GridCol span={5}>
						<Grid cols={5}>
							{/* form authoring assistant toggle */}
							<GridCol span={3}>
								<Text size="paragraph-2" weight="semi-bold" color="neutral-9">
									Form Authoring Assistant
								</Text>
								<Spacer height={1} />
								<div className={css.settingDescription}>
									<Text size="paragraph-3" weight="regular" color="neutral-9">
										Disabling this setting will hide the Form Authoring Assistant from users of the
										application.
									</Text>
								</div>
							</GridCol>

							<GridCol span={2}>
								<div data-testid="enable-toggle" className={css.toggleContainer}>
									<div className={css.toggleWrapper}>
										<Toggle onChange={(val) => handleEnabledToggle(val)} value={enabled || false} />
									</div>
								</div>
							</GridCol>

							{/* form question assistant toggle */}
							{formQuestionAssistantFeatureFlagEnabled && (
								<>
									<GridCol span={3}>
										<Text size="paragraph-2" weight="semi-bold" color="neutral-9">
											Form Question Assistant
										</Text>
										<Spacer height={1} />
										<div className={css.settingDescription}>
											<Text size="paragraph-3" weight="regular" color="neutral-9">
												Disabling this setting will hide the Form Question Assistant from users
												of the application.
											</Text>
										</div>
									</GridCol>

									<GridCol span={2}>
										<div data-testid="form-question-toggle" className={css.toggleContainer}>
											<div className={css.toggleWrapper}>
												<Toggle
													onChange={(val) => handleFormQuestionToggle(val)}
													value={formQuestionEnabled || false}
												/>
											</div>
										</div>
									</GridCol>
								</>
							)}

							{/* preferred writing style dropdown */}
							<GridCol span={3}>
								<Text
									className={css.textWithInfo}
									size="paragraph-2"
									weight="semi-bold"
									color="neutral-9"
								>
									Preferred Writing Style
								</Text>
								<Spacer height={1} />
								<div className={css.settingDescription}>
									<Text size="paragraph-3" weight="regular" color="neutral-9">
										Select a preferred writing style for the authoring assistant.
									</Text>
								</div>
							</GridCol>
							<GridCol span={2}>
								<SelectInput
									name="preferred-writing-style"
									isSearchable={false}
									value={state.preferredWritingStyle}
									results={preferredWritingStyleOptions}
									onChange={(val) => handlePreferredWritingStyleChange(val.value)}
								/>
							</GridCol>

							{/* disallowed word list input */}
							<GridCol span={5}>
								<Text
									className={css.textWithInfo}
									size="paragraph-2"
									weight="semi-bold"
									color="neutral-9"
								>
									Disallowed Word List
								</Text>
								<Spacer height={1} />
								<div className={css.settingDescription}>
									<Text size="paragraph-3" weight="regular" color="neutral-9">
										Prevent specific words from being included by the Authoring Assistant. Profanity
										is disallowed automatically.
									</Text>
								</div>
								<Spacer height={2} />
								<Container gap="8px" className={css.disallowedWordListInput}>
									<div className={css.inputField}>
										<InputText
											onEnter={handleAddDisallowedWord}
											// update input value, removing leading whitespace
											onChange={(input) => setDisallowedWordsInput(input.replace(/^\s+/, ""))}
											value={disallowedWordsInput}
											placeholder="Press 'enter' or click '+add' button"
										/>
									</div>
									<Button
										disabled={!disallowedWordsInput}
										className={css.addWordButton}
										icon={faPlus}
										onClick={handleAddDisallowedWord}
									>
										Add
									</Button>
								</Container>
								<Container
									id="disallowed-chips-container"
									marginTop="8px"
									gap="4px"
									testId="disallowed-words"
									wrap
								>
									{state.disallowedWords?.map((word) => (
										<DeletableChip
											className={css.disallowedWordChip}
											key={word}
											onDelete={() => handleDeleteDisallowedWord(word)}
											label={word}
											color="blue"
											// maxWidth is set to the width of the container to prevent the chip from wrapping
											// -50 to allow for ellipses space
											maxWidth={disallowedContainerWidth ? disallowedContainerWidth - 50 : 0}
										/>
									))}
								</Container>
							</GridCol>
						</Grid>
					</GridCol>
				</Grid>
				<Spacer height={5} />
				<div className={css.controlsContainer}>
					{hasUnsavedChanges ? (
						<span data-testid="discard-changes">
							<Button className={css.controlButton} isHollow={true} onClick={discardChanges}>
								Discard
							</Button>
						</span>
					) : null}

					<span data-testid="save-changes">
						<Button
							isRequesting={isPending}
							className={css.controlButton}
							color="green"
							onClick={handleSave}
							disabled={!hasUnsavedChanges}
						>
							Save Changes
						</Button>
					</span>
				</div>
			</AdminContainer>
		</Sentry.ErrorBoundary>
	);
};

export default AIAuthoringAssistantSettings;
